import axios from "axios";
import set from "lodash/fp/set";
import { useEffect, useMemo } from "react";

import { submitEvent } from "../../utils/tracking";
import { Loader } from "../../components";
import { RFP_STATUSES, type RFPStatus } from "../../constants/rfp";
import { useAnalyticsEvents } from "../../hooks/useAnalyticsEvents";
import { useRfpActivityHeartbeat } from "../../hooks/useRfpActivityHeartbeat";
import useToastNotificationService from "../../hooks/useToastNotificationService";
import { useUpdateRfp } from "../../hooks/useUpdateRfp";
import { formatDate, isUTC } from "../../utils.js";

import { NewAnalystRfpDetails } from "./NewAnalystRfpDetails";
import { useFinalDecisionQuery } from "./components/FinalDecisions/useFinalDecisionApi";
import { RFPDetailsState } from "./types";

interface Props {
  rfpId: string;
  state: RFPDetailsState;
  dispatch: React.Dispatch<Partial<RFPDetailsState>>;
  refetchRpfDetails: VoidFunction;
}

function AnalystRFPDetails(props: Props): React.JSX.Element {
  const { state, refetchRpfDetails, dispatch } = props;
  const {
    rfpDetails,
    analystDocsFolderUrl,
    status,
    clientName,
    brokerName,
    ecoRfpId,
    brokerExternalId,
  } = getStateValues(state);
  const { trackRfpStatusChange } = useAnalyticsEvents();
  // Mounted here, the element of the analyst /rfps/:id route, so one mount
  // covers every page under it.
  useRfpActivityHeartbeat(state.data);
  const { createToastNotification } = useToastNotificationService();

  const updatedStatusState = (newStatus: string): void => {
    dispatch(set("data.rfp.details.status", newStatus, state));
  };

  const stageChangeMutation = useUpdateRfp({
    rfpId: state.data?.rfp?.id,
    onMutate: ({ status: newStatus }) => {
      const previousStatus = status;
      trackRfpStatusChange({
        prevStatus: previousStatus || "",
        newStatus: RFP_STATUSES[newStatus as RFPStatus],
      });

      if (state.data?.rfp) {
        updatedStatusState(newStatus);
      }

      // return context for rollback
      return { previousStatus };
    },
    onSuccess: () => {
      refetchRpfDetails();
      createToastNotification({
        severity: "success",
        message: "Status Successfully Updated",
      });
    },
    onError: (err, _, context) => {
      // Rollback to previous status on error
      if (context?.previousStatus && state.data?.rfp) {
        updatedStatusState(context.previousStatus);
      }

      let message = "An error occurred while processing the stage change.";
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          | { errorMessage?: string }
          | undefined;
        message = data?.errorMessage ?? err.message;
      }
      createToastNotification({
        severity: "error",
        message,
      });
    },
  });

  const { effectiveDate, dueDate } = useMemo<{
    effectiveDate: string;
    dueDate: string;
  }>(() => {
    const isEffectiveDateUTC = isUTC(
      state.data?.revisedEffectiveDate || rfpDetails?.effectiveDate,
    );
    const isDueDateUTC = isUTC(
      state.data?.revisedDueDate || rfpDetails?.dueDate,
    );
    return {
      effectiveDate:
        formatDate(
          state.data?.revisedEffectiveDate || rfpDetails?.effectiveDate,
          isEffectiveDateUTC,
        ) || "",
      dueDate:
        formatDate(
          state.data?.revisedDueDate || rfpDetails?.dueDate,
          isDueDateUTC,
        ) || "",
    };
  }, [
    rfpDetails?.effectiveDate,
    rfpDetails?.dueDate,
    state.data?.revisedDueDate,
    state.data?.revisedEffectiveDate,
  ]);

  useEffect(() => {
    if (!state.loading && state.data) {
      submitEvent("RFP Details Page Viewed", {
        rfpId: state.data?.rfp?.id,
        clientId: state.data?.clientId,
        clientName: state.data?.rfp?.details?.clientInfo?.name,
        brokerName: state.data.broker?.name,
      });
    }
  }, [state.data, state.loading]);

  const { data: finalDecisionData } = useFinalDecisionQuery({
    rfpId: props.rfpId,
  });
  const hasFinalDecisions = !!finalDecisionData?.coverageDecisions?.length;

  if (state.loading) {
    return <Loader />;
  }

  if (!state.data) {
    return null;
  }

  return (
    <NewAnalystRfpDetails
      clientName={clientName}
      brokerName={brokerName}
      effectiveDate={effectiveDate}
      dueDate={dueDate}
      refetchRpfDetails={refetchRpfDetails}
      analystDocsFolderUrl={analystDocsFolderUrl}
      status={status}
      ecoRfpId={ecoRfpId}
      brokerExternalId={brokerExternalId}
      isStatusChanging={stageChangeMutation.isPending}
      hasFinalDecisions={hasFinalDecisions}
      onStatusChange={stageChangeMutation.mutate}
      state={state}
    />
  );
}

const getStateValues = (
  state: RFPDetailsState,
): {
  rfpDetails: RFPDetailsState["data"]["rfp"]["details"];
  analystDocsFolderUrl: string;
  status: string;
  clientName: string;
  brokerName: string;
  ecoRfpId: string;
  brokerExternalId: string;
} => {
  const clientInfo = state.data?.rfp?.details.clientInfo;
  const rfpDetails = state.data?.rfp?.details;
  const rfpData = state.data;
  const brokerInfo = state.data?.broker;
  const analystDocsFolderUrl = state.data?.analystDocsFolderUrl;
  const status = rfpDetails?.status;
  const clientName = clientInfo?.name || "";
  const brokerName = brokerInfo?.name || "";
  const ecoRfpId = rfpData?.ecoRfpId || "";
  const brokerExternalId = brokerInfo?.externalId;
  return {
    rfpDetails,
    analystDocsFolderUrl,
    status,
    clientName,
    brokerName,
    ecoRfpId,
    brokerExternalId,
  };
};

export default AnalystRFPDetails;
